import React, { FC, useState } from 'react';
import {
  statusMap,
  StatusPill,
} from '@dynamika-shared/ui/StatusPill/StatusPill';
import { Popover, Skeleton, Tooltip } from 'antd';
import { QcStatusEnum } from '@dynamika-shared/types/status';
import { CheckMarkIcon } from '@dynamika-shared/ui/icons';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { twMerge } from '@utils/twMerge';
import { useParams } from 'react-router-dom';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { useQCTaskSlice } from '@restore/QCTask/QCTask.slice';

export const confirmationStatuses = [QcStatusEnum.DONE, QcStatusEnum.FAILED];

export const QcVisitStatus: FC<{
  taskIsCompleted: boolean;
  isTaskAssignedToCurrentUser: boolean;
}> = ({ taskIsCompleted, isTaskAssignedToCurrentUser }) => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [statusState, setStatusState] = useState<QcStatusEnum | ''>('');

  const [taskData, changeStatus, statusLoading] = useQCTaskSlice(
    (state, actions) => [
      state.data,
      actions.changeStatus,
      state.changeStatusLoading,
    ]
  );

  const { id: clinicalTrialId, taskId } = useParams<{
    id: string;
    taskId: string;
  }>();
  const popoverIsDisabled = taskIsCompleted || !isTaskAssignedToCurrentUser;
  const statusPopoverIsDisabled = {
    ...(popoverIsDisabled && { open: false }),
  };

  const changeStatusSubmit = async (status: QcStatusEnum) => {
    if (clinicalTrialId && taskId && !statusLoading) {
      changeStatus({ clinicalTrialId, taskId, status });
      setStatusState('');
      setOpenDropdown(false);
    }
  };

  let tooltipText;

  if (taskIsCompleted) {
    tooltipText = 'Task is completed';
  } else if (!isTaskAssignedToCurrentUser) {
    tooltipText = 'Only assignee can change task status';
  } else {
    tooltipText = undefined;
  }

  const handleStatusChange = async (status: QcStatusEnum) => {
    if (confirmationStatuses.includes(status)) {
      setStatusState(status);
    } else {
      await changeStatusSubmit(status);
    }
  };
  return (
    <div className='flex items-center'>
      {taskData?.status ? (
        <>
          <span className='mr-1 text-dimGray'>Status:</span>
          <StatusPill status={taskData?.status} textCombiner={' '} />
        </>
      ) : (
        <Skeleton.Input active size={'small'} />
      )}

      <Popover
        open={openDropdown}
        {...statusPopoverIsDisabled}
        onOpenChange={open => !popoverIsDisabled && setOpenDropdown(open)}
        placement='bottom'
        content={
          <div>
            {[
              QcStatusEnum.IN_PROGRESS,
              QcStatusEnum.ON_HOLD,
              QcStatusEnum.QUERY_OPEN,
              QcStatusEnum.DONE,
              QcStatusEnum.FAILED,
            ].map(status => (
              <div
                key={status}
                onClick={async () => {
                  if (status !== taskData?.status) {
                    await handleStatusChange(status);
                  }
                  setOpenDropdown(false);
                }}
                onKeyDown={() => {}}
                className='flex cursor-pointer items-center rounded-full hover:bg-waterLight'
              >
                <StatusPill
                  className='my-1 hover:bg-waterLight  hover:text-black'
                  status={status}
                  dotted
                  textCombiner={' '}
                >
                  {status === taskData?.status && (
                    <CheckMarkIcon className='text-[24px]' />
                  )}
                </StatusPill>
              </div>
            ))}
          </div>
        }
        trigger='click'
      >
        <Tooltip
          color='white'
          title={
            tooltipText ? (
              <span className='text-1424 text-black'>{tooltipText}</span>
            ) : undefined
          }
        >
          <div>
            <ChevronIcon
              className={twMerge(
                'b-1 h-2 w-2 cursor-pointer rounded-full border',
                taskIsCompleted && 'opacity-30'
              )}
              classNameButton='h-3 w-3'
              dir={openDropdown ? 'up' : 'down'}
            />
          </div>
        </Tooltip>
      </Popover>
      <Modal
        zIndex={9999}
        width={440}
        onCancel={() => setStatusState('')}
        onOk={() => {
          changeStatusSubmit(statusState as QcStatusEnum);
        }}
        okProps={{ danger: true }}
        okText='Yes'
        open={!!statusState}
      >
        <span>
          <span>Are you sure you want to change the visit QC status to </span>
          <span
            className={twMerge(statusMap[statusState], 'capitalize bg-white')}
          >
            {statusState?.toLowerCase()}
          </span>
          <span>
            ? You will no longer be able to update the visit QC status and
            further uploads will not be allowed for this visit.
          </span>
        </span>
      </Modal>
    </div>
  );
};
